import { type FC, useEffect, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useIsLawyer, clearCachedLawyerStatus } from "@/shared/hooks/useIsLawyer";
import CircularProgress from "@/shared/components/CircularProgress";
import FullscreenLayout from "@/pages/layouts/FullScreenLayout";
import { POST_LOSS_ROUTES, ONBOARDING_ROUTES, LAWYER_ROUTES } from "@/shared/constants/paths";
import { getGoogleOAuthContext, clearGoogleOAuthContext } from "@/shared/utils/googleOAuth";
import { supabase } from "@/shared/lib/supabase";

import { LawyerApplicationPending } from "./LawyerApplicationPending";

interface UserCustomizations {
  lawyer_application?: { status?: string };
}

const RootRedirect: FC = () => {
  const { authUser, user, person, isLoading } = useAppContext();
  const { isLawyer, isLoading: lawyerLoading } = useIsLawyer();
  const navigate = useNavigate();

  const customizations = user?.customizations as UserCustomizations | undefined;
  const hasPendingLawyerApplication = customizations?.lawyer_application?.status === "pending";

  const hasShownWelcomeToast = useRef(false);

  useEffect(() => {
    if (authUser && !isLoading && !hasPendingLawyerApplication && !hasShownWelcomeToast.current) {
      hasShownWelcomeToast.current = true;
      enqueueSnackbar("Welcome back", {
        variant: "success",
        autoHideDuration: 3000,
      });
    }
  }, [authUser, isLoading, hasPendingLawyerApplication]);

  const handleSignOut = async () => {
    clearCachedLawyerStatus();
    await supabase.auth.signOut();
    navigate(LAWYER_ROUTES.LOGIN);
  };

  if (isLoading || lawyerLoading) {
    return (
      <FullscreenLayout>
        <CircularProgress />
      </FullscreenLayout>
    );
  }

  const oauthContext = getGoogleOAuthContext();
  if (
    oauthContext &&
    authUser &&
    (oauthContext.type === "lawyer-signup" || oauthContext.type === "lawyer-login")
  ) {
    if (oauthContext.type === "lawyer-signup" && authUser.email) {
      sessionStorage.setItem("lawyer_signup_email", authUser.email);
    }
    clearGoogleOAuthContext();
    return <Navigate to={oauthContext.redirectPath} replace />;
  }

  if (oauthContext && !authUser) {
    clearGoogleOAuthContext();
  }

  if (authUser && isLawyer) {
    return <Navigate to={LAWYER_ROUTES.DASHBOARD} replace />;
  }

  if (authUser && user && !person && hasPendingLawyerApplication) {
    return <LawyerApplicationPending onSignOut={handleSignOut} />;
  }

  if (authUser && user && person && user.completed_onboarding) {
    return <Navigate to={POST_LOSS_ROUTES.OVERVIEW} replace />;
  }

  return <Navigate to={ONBOARDING_ROUTES.ACCOUNT} replace />;
};

export default RootRedirect;
